/*
tslint:disable:import-spacing no-inferrable-types
variable-name align no-trailing-whitespace component-class-suffix
no-string-literal component-selector
*/

import {
  AfterViewInit,
  OnDestroy,
  Component,
  EventEmitter,
  Input,
  Output
} from '@angular/core';

import {
  BehaviorSubject,
  Subscription
} from 'rxjs';

import {
  WorkBlock
} from '@/models/scenario/work.block';

import {
  Diversion,
  DiversionType
} from '@/models/scenario/diversion';

import {Scenario}                     from '@/models/scenario/scenario';
import {Logger}                       from '@/util/logger';
import {UnDoRedo}                     from '@/util/un.do.redo';
import {DecisionPoint}                from '@/models/scenario/decision.point';
import {Track}                        from '@/models/scenario/track';
import {Train}                        from '@/models/scenario/train';
import {TrainsRenderer}               from '@/helpers/trains.renderer';
import {DiversionTrainsEditor}        from '@/screens/ide/diversion/diversion.trains.editor';
import {WorkDecisionPointRenderer}    from '@/screens/ide/workblox/work.decision.point.renderer';
import {DiversionDecisionPointEditor} from '@/screens/ide/diversion/diversion.decision.point.editor';
import { TrackRenderer }              from '@/helpers/track.renderer';
import { DiversionTrackEditor }       from '@/helpers/diversion.track.editor';
import {DiversionWorkRenderer}        from '@/screens/ide/diversion/diversion.work.renderer';
import {DiversionWorkEditor}          from '@/screens/ide/diversion/diversion.work.editor';
import {DiversionNameRenderer}        from '@/screens/ide/diversion/diversion.name.renderer';
import {DescriptionRenderer}          from '@/helpers/description.renderer';
import {NumberRenderer}               from '@/helpers/number.renderer';
import {NumberEditor}                 from '@/helpers/number.editor';
import {DiversionChange}              from '@/screens/ide/diversion/diversion.change';

import {IDiversionInputCapture}       from './i.diversion.input.capture';

import {ICommand}                     from '@/util/i.command';
import {SetNameCommand}               from './set.name.command';
import {SetDescriptionCommand}        from './set.description.command';
import {SetTrainsCommand}             from './set.trains.command';
import {SetDurationCommand}           from './set.duration.command';
import {SetStdDevCommand}             from './set.std.dev.command';
import {SetWorkCommand}               from './set.work.command';
import {SetStartLocCommand}           from './set.start.loc.command';
import {SetStartTrackCommand}         from './set.start.track.command';
import {SetEndLocCommand}             from './set.end.loc.command';
import {SetEndTrackCommand}           from './set.end.track.command';

import {DiversionInlineEdit}          from './diversion.inline.edit';

import 'ag-grid-community';
import 'ag-grid-enterprise';

import {TrackIngress}                 from "@/helpers/track.ingress";

import {
  CellClassParams,
  CellPosition,
  CellValueChangedEvent,
  Column,
  GetContextMenuItemsParams,
  GridApi,
  GridReadyEvent,
  IRowNode,
  TabToNextCellParams
} from 'ag-grid-community';

@Component({
  selector: 'diversion-editor',
  template: `
    <div
      id="diversion-container-wrapper_{{uuid}}"
      class="diversion-container-wrapper"
      style="display: block; float: left; padding: 8px; width: 100%; height: 100%;">

      <span style="height: 32px; width: 100%; display: block;" [innerHTML]="errorText"></span>
      <ag-grid-angular
        id="ag-grid-diversion-editor_{{uuid}}" style="width: 100%; height: calc(100% - 32px);"
        class="ag-theme-balham-dark ag-grid-diversion-editor"
        [rowData]="rowData"
        [columnDefs]="columnDefs"
        [defaultColDef]="defaultColDef"
        [context]="context"
        [animateRows]="true"
        [enableRangeSelection]="true"

        [allowContextMenuWithControlKey]="true"
        [getContextMenuItems]="getContextMenuItems"

        [tabToNextCell]="tabToNextCell"

        [components]="components"

        (gridReady)="onGridReady($event)"

      >
      </ag-grid-angular>
    </div>

   `
})

/**
 *
 * @class DiversionEditor
 *
 * @brief editor for just the diversions section of a scenario
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      July 2020
 *
 */
export class DiversionEditor implements AfterViewInit, OnDestroy, IDiversionInputCapture {

  public          errorText: string                               = '<i class="wbedit-info-line">Ready.</i>';

  @Input()             uuid: number                               = 0;

  /** the history manager (not owned by us */
  private          _history: UnDoRedo                             = null;

  /** @var _scenario the scenario model */
  private         _scenario: Scenario                             = null;

  /** the currently active row/selection/Diversion */
  private selectedDiversion: BehaviorSubject<DiversionChange>     = null;
  private         selectSub: Subscription                         = null;

  private    lastInlineEdit: BehaviorSubject<DiversionInlineEdit> = null;
  private           editSub: Subscription                         = null;

  /** validation related flags */
  private       _inputValid: boolean                              = false;
  private        _canDelete: boolean                              = false;
  private         _hasInput: boolean                              = false;

  /** the API for the widget */
  private               api: GridApi                              = null;

  private      isFullScreen: boolean                              = false;

  public context  = {

    diversion: this

  };

  public defaultColDef           = {
    sortable:  true,
    filter:    true,
    resizable: true
  };

  public rowData                 = [{
    id:          '',
    row:         0,
    name:        '',
    trains:      [],
    work:        null,
    start_loc:   null,
    start_track: null,
    end_loc:     null,
    end_track:   null,
    duration:    0,
    stddev:      0,
    description: ''
  }];

  @Output() edit                 = new EventEmitter<Scenario>();

  public components = {
    dpEditor:     DiversionDecisionPointEditor,
    workEditor:   DiversionWorkEditor,
    trainEditor:  DiversionTrainsEditor,
    trackEditor:  DiversionTrackEditor,
    numberEditor: NumberEditor
  };

  public columnDefs: any[]       = [

    {
      headerName: 'ID',
      field: 'id',
      editable: false,
      width: 72
    },

    {
      headerName: 'Name',
      field: 'name',
      cellRenderer: DiversionNameRenderer,

      onCellValueChanged: (e: CellValueChangedEvent) => {

        e.context.diversion.nameChanged(e.oldValue, e.newValue);
        e.context.diversion.validate();
      },
      cellClassRules: {
        'work-input-error': (params: CellClassParams) => {

          return ((typeof params.value === 'undefined') ||
            (params.value === null) ||
            (params.value.trim() === ''));
        }
      },
      editable: true,
      width: 150,
    },

    {
      headerName: 'Trains',
      field: 'trains',
      cellRenderer: TrainsRenderer,
      editable: true,
      width: 150,
      cellEditorParams: {
        cellRenderer: TrainsRenderer,
      },
      cellEditor:   'trainEditor',
      equals:       (a: Train[], b: Train[]): boolean =>  {

        if (!Array.isArray(a) || !Array.isArray(b)) {

          return a === b;
        }

        const aa:number[] = a.map(x => x._id);
        const bb:number[] = b.map(x => x._id);

        aa.sort();
        bb.sort();

        return JSON.stringify(aa) === JSON.stringify(bb);
      },

      onCellValueChanged: (e: CellValueChangedEvent) => {

        e.context.diversion.trainsChanged(e.oldValue, e.newValue);
        e.context.diversion.validate();
      },
      cellClassRules: {
        'work-input-error': (params: CellClassParams) => {

          if (params.value === '') {

            return true;
          }

          if (Array.isArray(params.value) && params.value.length === 0) {

            return true;
          }

          return ((typeof params.value === 'undefined') || (params.value === null));
        }
      }
    },

    {
      headerName: 'Work Block',
      field: 'work',
      cellRenderer: DiversionWorkRenderer,
      cellEditor:   'workEditor',
      onCellValueChanged: (e: CellValueChangedEvent) => {

        e.context.diversion.workChanged(e.oldValue, e.newValue);
        e.context.diversion.validate();
      },
      cellClassRules: {
        'work-input-error': (params: CellClassParams) => {

          if (params.value === '') {

            return true;
          }

          return ((typeof params.value === 'undefined') || (params.value === null));
        }
      },
      editable: true,
      width: 150,
    },

    {
      headerName: 'Start',
      field: 'start_loc',
      cellRenderer: WorkDecisionPointRenderer,
      cellEditor:   'dpEditor',
      equals:       (a: DecisionPoint, b: DecisionPoint): boolean =>  {

        if (!( a instanceof DecisionPoint ) || !( b instanceof DecisionPoint )) {

          return a === b;
        }

        return a._id === b._id;
      },

      onCellValueChanged: (e: CellValueChangedEvent) => {

        e.context.diversion.startLocChanged(e.oldValue, e.newValue);

        const        row: IRowNode = e.context.diversion.firstRow();
        let        track: Track   = null;
        const diversions: Track[] = e.newValue ? e.newValue.diversionTracks() : [];

        if (diversions.length === 1) {

          track = diversions[0];
        }

        /* if there is only one possible diversion track...then just use that. */

        this.setStartTrack(new TrackIngress(e.newValue, track));

        /* force tabbing into the track */

        const de = e.context.diversion;

        setTimeout(() => {

          let     cols: Column[] = de.api.getAllDisplayedColumns();
          let trackCol: Column   = cols[5];

          de.api.ensureColumnVisible(trackCol);
          de.api.setFocusedCell(0, trackCol);

        }, 100);

      },
      cellClassRules: {
        'work-input-error': (params: CellClassParams) => {

          if (params.value === '') {

            return true;
          }

          return ((typeof params.value === 'undefined') || (params.value === null));
        }
      },
      editable: true,
      width: 150
    },
    {
      headerName: 'Start Track',
      field: 'start_track',
      cellRenderer: TrackRenderer,
      cellEditor:   'trackEditor',

      equals:       (a: TrackIngress, b: TrackIngress): boolean =>  {

        if ((a === null) || (b === null)) {

          return a === b;
        }

        if (!( a instanceof TrackIngress ) || !( b instanceof TrackIngress )) {

          if ((a.track === null) || (b.track === null)) {

            return a.track === b.track;
          }

          return a === b;
        }

        if ((a.track === null) || (b.track === null)) {

          return a.track === b.track;
        }

        return a.track._id === b.track._id;
      },

      onCellValueChanged: (e: CellValueChangedEvent) => {

        e.context.diversion.startTrackChanged(e.oldValue, e.newValue);
        e.context.diversion.validate();

      },
      cellClassRules: {
        'work-input-error': (params: CellClassParams) => {

          if (params.value instanceof TrackIngress) {

            return params.value.track === null;
          }

          if (params.value === '') {

            return true;
          }

          return ((typeof params.value === 'undefined') || (params.value === null));
        }
      },
      editable: true,
      width: 150,
      columnGroupShow: 'open'
    },

    {
      headerName: 'End',
      field: 'end_loc',
      cellRenderer: WorkDecisionPointRenderer,
      cellEditor:   'dpEditor',
      equals:       (a: DecisionPoint, b: DecisionPoint): boolean =>  {

        if (!( a instanceof DecisionPoint ) || !( b instanceof DecisionPoint )) {

          return a === b;
        }

        return a._id === b._id;
      },

      onCellValueChanged: (e: CellValueChangedEvent) => {

        e.context.diversion.endLocChanged(e.oldValue, e.newValue);

        const        row: IRowNode = e.context.diversion.firstRow();
        let        track: Track   = null;
        const diversions: Track[] = e.newValue.diversionTracks();

        if (diversions.length === 1) {

          track = diversions[0];
        }

        /* if there is only one possible diversion track...then just use that. */

        this.setEndTrack(new TrackIngress(e.newValue, track));

        /* force tabbing into the track */

        const de = e.context.diversion;

        setTimeout(() => {

          let     cols: Column[] = de.api.getAllDisplayedColumns();
          let trackCol: Column   = cols[7];

          de.api.ensureColumnVisible(trackCol);
          de.api.setFocusedCell(0, trackCol);

        }, 100);

      },
      cellClassRules: {
        'work-input-error': (params: CellClassParams) => {

          if (params.value === '') {

            return true;
          }

          return ((typeof params.value === 'undefined') || (params.value === null));
        }
      },
      editable: true,
      width: 150
    },
    {
      headerName: 'End Track',
      field: 'end_track',
      cellRenderer: TrackRenderer,
      cellEditor:   'trackEditor',

      equals:       (a: TrackIngress, b: TrackIngress): boolean =>  {

        if ((a === null) || (b === null)) {

          return a === b;
        }

        if (!( a instanceof TrackIngress ) || !( b instanceof TrackIngress )) {

          if ((a.track === null) || (b.track === null)) {

            return a.track === b.track;
          }

          return a === b;
        }

        if ((a.track === null) || (b.track === null)) {

          return a.track === b.track;
        }

        return a.track._id === b.track._id;
      },

      onCellValueChanged: (e: CellValueChangedEvent) => {

        e.context.diversion.endTrackChanged(e.oldValue, e.newValue);
        e.context.diversion.validate();

      },
      cellClassRules: {
        'work-input-error': (params: CellClassParams) => {

          if (params.value instanceof TrackIngress) {

            return params.value.track === null;
          }

          if (params.value === '') {

            return true;
          }

          return ((typeof params.value === 'undefined') || (params.value === null));
        }
      },
      editable: true,
      width: 150,
      columnGroupShow: 'open'
    },

    {
      headerName: 'Duration',
      field: 'duration',
      cellRenderer: NumberRenderer,
      cellEditor: 'numberEditor',
      onCellValueChanged: (e: CellValueChangedEvent): void => {

        e.context.diversion.durationChanged(e.oldValue, e.newValue);
        e.context.diversion.validate();
      },
      cellClassRules: {
        'work-input-error': (params: CellClassParams): boolean => {

          if (typeof params.value === 'number') {

            return params.value <= 0;
          }

          return ((typeof params.value === 'undefined') ||
            (params.value === null) ||
            (params.value.trim() === '') ||
            (params.value <= 0));
        }
      },
      editable: true,
      width: 150,
    },

    {
      headerName: 'Std Dev.',
      field: 'stddev',
      cellRenderer: NumberRenderer,
      cellEditor: 'numberEditor',
      onCellValueChanged: (e: CellValueChangedEvent) => {

        e.context.diversion.stdDevChanged(e.oldValue, e.newValue);
        e.context.diversion.validate();
      },
      cellClassRules: {
        'work-input-error': (params: CellClassParams) => {

          if (typeof params.value === 'number') {

            return params.value < 0;
          }

          return ((typeof params.value === 'undefined') ||
            (params.value === null) ||
            (params.value.trim() === '') ||
            (params.value < 0));
        }
      },
      editable: true,
      width: 150,
    },

    {
      headerName:   'Description',
      cellRenderer: DescriptionRenderer,
      field:        'description',

      onCellValueChanged: (e: CellValueChangedEvent) => {

        e.context.diversion.descriptionChanged(e.oldValue, e.newValue);
        e.context.diversion.validate();
      },

      editable:     true,
      width:        150
    }

  ];

  constructor(

  ) {

  }

  /** update the error line in the loader drop down */
  public status(msg: string =  '') {

    this.errorText = `<i class="wbedit-info-line">${msg}</i>`;
    Logger.info(`[diversioneditor] ${msg}`);
  }

  /** update the error line in the loader drop down */
  public success(msg: string =  '') {

    this.errorText = `<i class="wbedit-success-line">${msg}</i>`;
    Logger.debug(`[diversioneditor] ${msg}`);
  }

  /** update the error line in the loader drop down */
  public error(msg: string =  '') {

    this.errorText = `<i class="wbedit-error-line">${msg}</i>`;
    Logger.error(`[diversioneditor] ${msg}`);
  }

  /** scan all the editor inputs, and set up our validity flags */
  private validate(): void {

    const  fields: string[] = [
      'name',
      'trains',
      'work',
      'start_loc',
      'start_track',
      'end_loc',
      'end_track',
      'duration',
      'stddev'
    ];
    const     row: IRowNode  = this.firstRow();
    let   invalid: boolean  = false;

    this.status('Validating...');

    /* if it has non-zero diversion id...then its one we can either clone or delete */

    this._canDelete = this.currentId() > 0;

    /* if any of the inputs are non-empty (not '', null or undefined), then we have "some" input */

    this._hasInput = false;

    fields.forEach((item: string) => {

      const datum: any = row.data[item];

      if (datum instanceof TrackIngress) {

        if ((typeof datum.track === 'undefined') || (datum.track === null)) {

          this.error(`${item} can not be empty.`);
          invalid = true;
          return ;
        }
      }

      if (datum === '') {

        this.error(`${item} can not be empty.`);
        invalid = true;
        return ;
      }

      if (typeof datum === 'undefined') {

        this.error(`${item} can not be empty.`);
        invalid = true;
        return ;
      }

      if (datum === null) {

        this.error(`${item} can not be empty.`);
        invalid = true;
        return ;
      }

      if (Array.isArray(datum) && (datum.length === 0)) {

        this.error(`${item} (list) can not be empty.`);

        invalid = true;
        return ;
      }

      if ((item === 'duration') && ((datum === '0') || (datum === ''))) {

        this.error(`${item} must be non-zero number.`);

        invalid = true;
        return ;
      }

      if ((item === 'stddev') && ((datum < 0) || (datum === ''))) {

        this.error(`${item} must be positive number.`);

        invalid = true;
        return ;
      }

      /* at least one column has non-empty input */

      this._hasInput = true;
    });

    if (!invalid) {

      this.success('Validated: OK.');
    }

    /* the input is only valid if all inputs are filled in */

    this._inputValid = !invalid;
  }

  /** check to see if the input is valid (can add as WorkBlock) */
  public isValid(): boolean {

    return this._inputValid;
  }

  /** check to see if the input is an edit of an existing workblock (i.e. ID > 0) */
  public isDeletable(): boolean {

    return this._canDelete;
  }

  /** check to see if the there is any input, either user entered or a workblock being edited. */
  public hasInput(): boolean {

    return this._hasInput || this._canDelete;
  }

  tabToNextCell(params: TabToNextCellParams): CellPosition  {

    /* if we're going outside the table...stay on target, stay on target! */

    if ( (typeof params.nextCellPosition === 'undefined') || (params.nextCellPosition === null)) {

      return params.previousCellPosition;
    }

    return params.nextCellPosition;
  }

  onGridReady(params: GridReadyEvent): void {

    this.api       = params.api;

    this.focusCell('name');
  }

  /** capture the current user inputs */
  captureInput(): DiversionChange {

    /* there is only one row, get the data from the row, and convert to a snap shot */

    const snapshot: DiversionChange = new DiversionChange();
    const row: IRowNode              = this.firstRow();

    snapshot.id          = row.data.id          ? row.data.id       : '';
    snapshot.name        = row.data.name        ? row.data.name     : '';
    snapshot.type        = DiversionType.OneWay;
    snapshot.work        = row.data.work        ? row.data.work        : null;
    snapshot.start       = row.data.start_loc   ? row.data.start_loc   : null;
    snapshot.startTrack  = Object.assign({}, row.data.start_track);
    snapshot.end         = row.data.end_loc     ? row.data.end_loc     : null;
    snapshot.endTrack    = Object.assign({}, row.data.end_track);
    snapshot.duration    = row.data.duration    ? row.data.duration    : 0;
    snapshot.stdDev      = row.data.stddev      ? row.data.stddev      : 0;
    snapshot.trains      = row.data.trains      ? row.data.trains      : [];
    snapshot.description = row.data.description ? row.data.description : '';

    /* pass it back! */

    return snapshot;
  }

  /** helper to focus on the given column / field */
  focusCell(colName: string): void {

    let     cols: Column[]  = this.api.getAllDisplayedColumns();
    let col: Column         = cols.find((x: Column): boolean => x.getId() === colName);

    /* find the column ... */

    if ((col === null) || (typeof col === 'undefined')) {

      return ;
    }

    /* focus on that cel */

    this.api.ensureColumnVisible(col);
    this.api.setFocusedCell(0, col);
  }

  /** restore the user input to what they are in this snapshot */
  restoreInput(snapshot: DiversionChange): void {

    this.status(`Loading input...`);

    Logger.debug('[diversioneditor] restoring input: ', snapshot);

    /* there is only one row, set the values from this snapshot, without change detection */

    /* create the row data (there is only one row!) */

    /* non-change detection update */

    const row: IRowNode = this.firstRow();

    row.data.id          = snapshot.id;
    row.data.name        = snapshot.name;
    row.data.type        = snapshot.type;
    row.data.work        = snapshot.work;
    row.data.start_loc   = snapshot.start;
    row.data.start_track = snapshot.startTrack;
    row.data.end_loc     = snapshot.end;
    row.data.end_track   = snapshot.endTrack;
    row.data.duration    = snapshot.duration;
    row.data.stddev      = snapshot.stdDev;
    row.data.trains      = snapshot.trains;
    row.data.description = snapshot.description;

    this.status(`Loaded.`);

    /* force a redraw, but without change detection (which could be an infinite loop) */

    this.api.redrawRows({rowNodes: [row]});
    this.validate();

  }

  /** handle single column edits in the edit input area */
  setInlineEditSubject(edit: BehaviorSubject<DiversionInlineEdit>) {

    if (this.editSub !== null) {

      /* unsubscribe old one */

      this.editSub.unsubscribe();
    }

    /* subscribe to this selection watcher ... */

    this.lastInlineEdit = edit;
    this.editSub        = null;

  }

  /** set a new observer to use for watching for selection changes */
  public setSelectionSubject(selection: BehaviorSubject<DiversionChange>) {

    if (this.selectSub !== null) {

      /* unsubscribe old one */

      this.selectSub.unsubscribe();
    }

    /* subscribe to this selection watcher ... */

    this.selectedDiversion = selection;
    this.selectSub         = selection.subscribe( (next: DiversionChange) => {

      this.setSelected(next);
    });

  }

  /** handle somebody change the selection somewhere */
  public setSelected(change: DiversionChange) {

    if (change === null) {

      /* just clear any open edits */

      this.clear();

      return ;
    }

    if (change.snapshot !== null) {

      /* this selection has a capture of previous user inputs, we use those instead */

      change = change.snapshot;
    }

    /* check that we aren't calling our self */

    if ( (change.src instanceof DiversionEditor) && (change.src === this)) {

      /* from self, ignore infinite looping! */

      return ;
    }

    /* handle the selection update */
    Logger.debug('[diversioneditor] selection change....');

    this.restoreInput(change);
  }

  public getContextMenuItems(params: GetContextMenuItemsParams): any {

    return [];
  }

  /** @brief first time setup of view */
  ngAfterViewInit(): void {

    Logger.debug('[diversioneditor] view init...');

    this.clear();

  }

  ngOnDestroy(): void {

    if (this.selectSub !== null) {

      /* unsubscribe our observer */

      this.selectSub.unsubscribe();
    }

    if (this.editSub !== null) {

      /* unsubscribe our observer */

      this.editSub.unsubscribe();
    }
  }

  /** clear the user input area */
  public clear() {

    Logger.debug('[diversioneditor] clearing...');

    if (!this.api) {

      /* api isn't even available yet */

      return ;
    }

    /* clearing just means all input columns are empty */

    this.api.setGridOption('rowData', [{
      id:          '',
      row:         0,
      name:        '',
      trains:      [],
      work:        null,
      start_loc:   null,
      start_track: null,
      end_loc:     null,
      end_track:   null,
      duration:    0,
      stddev:      0,
      description: ''
    }]);
  }

  /** fetch current row ID (if there is one */
  public currentId(): number {

    const row: IRowNode = this.firstRow();

    const result: number = row.data?.id;

    if (result === null) {

      return 0;
    }

    if (typeof result === 'undefined') {

      return 0;
    }

    if ( isNaN(result) ) {

      return 0;
    }

    /* looks legit, pass it back */

    return result;
  }

  /** fetch the first row */
  public firstRow(): IRowNode {

    const items: IRowNode[] = [];

    this.api.forEachNode((rowNode: IRowNode): void => {

      items.push(rowNode);
    });

    return items[0];
  }

  /** helper to fetch the actual diversion for the given row */
  public diversionOfRow(id: number): Diversion {

    if (this._scenario === null) {

      return null;
    }

    return Array.from(this._scenario.diversions().values()).find(x => x._id === id);
  }

  /** helper to update the actual WorkBlock model in the scenario */
  public updateModel(id: number, field: string, value: any) {

    const div: Diversion = this.diversionOfRow(id);

    if ((div === null) || (!div)) {

      /* can't find it */

      return ;
    }

    Logger.debug('[diversioneditor] updating model:', id, field, value);

    switch (field) {

      case 'type': {

          /* use the given type code (WorkType) */

          div._type = value as DiversionType;
        }
        break;

      case 'name': {

          /* use the given name string */

          div._name = value as string;
        }
        break;

      case 'description': {

          /* use the given description */

          div._description = value as string;
        }
        break;

      case 'start_loc': {

          div._from = value as DecisionPoint;
        }
        break;

      case 'start_track': {

          div._fromTrack = (value as TrackIngress).track;
        }
        break;

      case 'end_loc': {

          div._to = value as DecisionPoint;
        }
        break;

      case 'end_track': {

          div._toTrack = (value as TrackIngress).track;
        }
        break;

      case 'duration': {

          div._duration = value as number;
        }
        break;

      case 'stddev': {

          div._stddev = value as number;
        }
        break;

      case 'trains': {

          div._trains = value as Train[];
        }
        break;

      case 'work': {

          div._work = value as WorkBlock;
        }
        break;
    }

    const edit: DiversionInlineEdit =  new DiversionInlineEdit({
      id,
      field,
      value
    });

    /* publish the edit we just did...so that other views know to update themselves */

    this.lastInlineEdit.next(edit);

  }

  /** react to applicable work block change */
  public workChanged(a: WorkBlock, b: WorkBlock) {

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'work', b);
    }

    /* track the change */

    const cmd: ICommand = new SetWorkCommand(a, b, this);

    this._history.insert(cmd);

    /* publish the edit so other views can update themselves */


  }

  /** force the work begin column to be this value */
  public setWork(value: WorkBlock) {

    let datum: any = null;

    if (value instanceof WorkBlock) {

      datum = value;

    } else if ((typeof value === 'undefined') || (value === null) || (value === '')) {

      datum = '';

    } else {

      /* do nothing */

      return ;
    }

    /* get the live row */

    const row: IRowNode = this.firstRow();

    /* set this column */

    row.data.work = datum;

    /* force a redraw, but without change detection (which could be an infinite loop) */

    this.api.redrawRows({rowNodes: [row]});
    this.validate();

    this.focusCell('work');

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'work', datum);
    }
  }

  /** react to a starting track change */
  public startTrackChanged(a: TrackIngress, b: TrackIngress) {

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'start_track', b);
    }

    /* track the change */

    const cmd: ICommand = new SetStartTrackCommand(a, b, this);

    this._history.insert(cmd);

    /* publish the edit so other views can update themselves */



  }

  /** force the starting track */
  public setStartTrack(value: TrackIngress) {

    let datum: TrackIngress = value;

    if ((typeof value === 'undefined') || (value === null)) {

      datum = null;

    }

    /* get the live row */

    const row: IRowNode = this.firstRow();

    /* set this column */

    row.data.start_track = value;

    /* force a redraw, but without change detection (which could be an infinite loop) */

    this.api.redrawRows({rowNodes: [row]});
    this.validate();

    this.focusCell('start_track');

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'start_track', datum);
    }
  }

  /** react to a work block begin change */
  public startLocChanged(a: DecisionPoint, b: DecisionPoint): void {

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'start_loc', b);
    }

    /* track the change */

    const cmd: ICommand = new SetStartLocCommand(a, b, this);

    this._history.insert(cmd);

    /* publish the edit so other views can update themselves */



  }

  /** force the work begin column to be this value */
  public setStartLoc(value: DecisionPoint) {

    let datum: DecisionPoint = value;

    if ((typeof value === 'undefined') || (value === null)) {

      datum = null;

    }

    /* get the live row */

    const row: IRowNode = this.firstRow();

    /* set this column */

    row.data.start_loc = datum;

    /* force a redraw, but without change detection (which could be an infinite loop) */

    this.api.redrawRows({rowNodes: [row]});
    this.validate();

    this.focusCell('start');

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'start', datum);
    }
  }

  /** react to an ending track change */
  public endTrackChanged(a: TrackIngress, b: TrackIngress) {

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'end_track', b);
    }

    /* track the change */

    const cmd: ICommand = new SetEndTrackCommand(a, b, this);

    this._history.insert(cmd);

    /* publish the edit so other views can update themselves */



  }

  /** force the ending track */
  public setEndTrack(value: TrackIngress) {

    let datum: TrackIngress = value;

    if ((typeof value === 'undefined') || (value === null)) {

      datum = null;

    }

    /* get the live row */

    const row: IRowNode = this.firstRow();

    /* set this column */

    row.data.end_track = datum;

    /* force a redraw, but without change detection (which could be an infinite loop) */

    this.api.redrawRows({rowNodes: [row]});
    this.validate();

    this.focusCell('end_track');

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'end_track', datum);
    }
  }

  /** react to a work block begin change */
  public endLocChanged(a: DecisionPoint, b: DecisionPoint): void {

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'end_loc', b);
    }

    /* track the change */

    const cmd: ICommand = new SetEndLocCommand(a, b, this);

    this._history.insert(cmd);
  }

  /** force the work begin column to be this value */
  public setEndLoc(value: DecisionPoint): void {

    let datum: DecisionPoint = value;

    if ((typeof value === 'undefined') || (value === null)) {

      datum = null;

    }

    /* get the live row */

    const row: IRowNode = this.firstRow();

    /* set this column */

    row.data.end_loc = datum;

    /* force a redraw, but without change detection (which could be an infinite loop) */

    this.api.redrawRows({rowNodes: [row]});
    this.validate();

    this.focusCell('end');

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'end', datum);
    }
  }

  /** react to a diversion time change */
  public durationChanged(a: number, b: number): void {

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'duration', b);
    }

    /* track the change */

    const cmd: ICommand = new SetDurationCommand(a, b, this);

    this._history.insert(cmd);
  }

  /** force the diversion time */
  public setDuration(value: number) {

    let datum: any = null;

    if (typeof value === 'number') {

      datum = value;

    } else if ((typeof value === 'undefined') || (value === null) || (value === '')) {

      datum = '';

    } else {

      /* do nothing */

      return ;
    }

    /* get the live row */

    const row: IRowNode = this.firstRow();

    /* set this column */

    row.data.duration = datum;

    /* force a redraw, but without change detection (which could be an infinite loop) */

    this.api.redrawRows({rowNodes: [row]});
    this.validate();

    this.focusCell('duration');

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'duration', datum);
    }
  }

  /** react to a standard deviation change */
  public stdDevChanged(a: number, b: number) {

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'stddev', b);
    }

    /* track the change */

    const cmd: ICommand = new SetStdDevCommand(a, b, this);

    this._history.insert(cmd);
  }

  /** force the standard deviation */
  public setStdDev(value: number) {

    let datum: any = null;

    if (typeof value === 'number') {

      datum = value;

    } else if ((typeof value === 'undefined') || (value === null) || (value === '')) {

      datum = '';

    } else {

      /* do nothing */

      return ;
    }

    /* get the live row */

    const row: IRowNode = this.firstRow();

    /* set this column */

    row.data.stddev = datum;

    /* force a redraw, but without change detection (which could be an infinite loop) */

    this.api.redrawRows({rowNodes: [row]});
    this.validate();

    this.focusCell('stddev');

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'stddev', datum);
    }
  }

  /** react to a chaing in trains of diversion group */
  public trainsChanged(a: Train[], b: Train[]) {

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'trains', b);
    }

    /* track the change */

    const cmd: ICommand = new SetTrainsCommand(a, b, this);

    this._history.insert(cmd);
  }

  /** force the active days column to be this value */
  public setTrains(value: Train[]) {

    let datum: any = null;

    if (Array.isArray(value)) {

      datum = value;

    } else if (typeof value === 'undefined') {

      datum = [];

    } else {

      /* do nothing */

      return ;
    }

    /* get the live row */

    const row: IRowNode = this.firstRow();

    /* set this column */

    row.data.trains = datum;

    /* force a redraw, but without change detection (which could be an infinite loop) */

    this.api.redrawRows({rowNodes: [row]});
    this.validate();

    this.focusCell('trains');

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'trains', datum);
    }
  }

  /** react to a diversion name change */
  public nameChanged(a: string, b: string): void {

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'name', b);
    }

    /* track the change */

    const cmd: ICommand = new SetNameCommand(a, b, this);

    this._history.insert(cmd);
  }

  /** force the name column to be this value */
  public setName(value: string) {

    if (typeof value === 'undefined') {

      value = '';
    }

    /* get the live row */

    const row: IRowNode = this.firstRow();

    /* set this column */

    row.data.name = value;

    /* force a redraw, but without change detection (which could be an infinite loop) */

    this.api.redrawRows({rowNodes: [row]});
    this.validate();

    this.focusCell('name');

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'name', value);
    }
  }

  /** react to a diversion description change */
  public descriptionChanged(a: string, b: string): void {

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'description', b);
    }

    /* track the change */

    const cmd: ICommand = new SetDescriptionCommand(a, b, this);

    this._history.insert(cmd);
  }


  /** force the description column to be this value */
  public setDescription(value: string) {

    if (typeof value === 'undefined') {

      value = '';
    }

    /* get the live row */

    const row: IRowNode = this.firstRow();

    /* set this column */

    row.data.desc = value;

    /* force a redraw, but without change detection (which could be an infinite loop) */

    this.api.redrawRows({rowNodes: [row]});
    this.validate();

    this.focusCell('description');

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'description', value);
    }
  }

  /** take a reference to the shared history manager (provides undo/redo support) */
  public setHistory(history: UnDoRedo): void {

    this._history = history;
  }

  /** reset to this scenario */
  public setScenario(s: Scenario) {

    /* set the "model" */

    this._scenario = s;

    /* clear the input area */

    this.clear();

    this.status(`New scenario set.`);
  }

  /** fetch the current scenario (if there is one) */
  public getScenario(): Scenario {

    return this._scenario;
  }


}
